import React, { createContext, useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import { getSocketIoUrl } from '../config';
import { useAdminUsersApi } from './hooks/useAdminUsersApi';
import { useAdminStatsApi } from './hooks/useAdminStatsApi';
import { useAdminRequestsApi } from './hooks/useAdminRequestsApi';
import { useAdminAuditApi } from './hooks/useAdminAuditApi';

const STAFF_ROLES = ['admin', 'manager'];

const AdminSocketContext = createContext({
  socket: null,
  onlineUserIds: [],
});

export const useAdminSocketContext = () => useContext(AdminSocketContext);

export function AdminSocketProvider({ token, children }) {
  const role = useSelector((state) => state.interface.role);
  const [onlineUserIds, setOnlineUserIds] = React.useState([]);

  const adminSocketUrl = useMemo(() => getSocketIoUrl('/admin'), []);

  const socket = useMemo(() => {
    if (!token || !STAFF_ROLES.includes(role)) return null;
    return io(adminSocketUrl, {
      transports: ['websocket'],
      auth: { token: `Bearer ${token}` },
      reconnectionAttempts: 10,
      reconnectionDelay: 5000,
    });
  }, [token, role, adminSocketUrl]);

  const usersApi = useAdminUsersApi(socket);
  const statsApi = useAdminStatsApi(socket);
  const requestsApi = useAdminRequestsApi(socket);
  const auditApi = useAdminAuditApi(socket);

  useEffect(() => {
    if (!socket) return undefined;

    const onPresence = (data) => {
      setOnlineUserIds(data?.online || []);
    };

    socket.on('admin:presence:update', onPresence);
    return () => {
      socket.off('admin:presence:update', onPresence);
    };
  }, [socket]);

  const value = useMemo(
    () => ({
      socket,
      onlineUserIds,
      ...usersApi,
      ...statsApi,
      ...requestsApi,
      ...auditApi,
    }),
    [socket, onlineUserIds, usersApi, statsApi, requestsApi, auditApi]
  );

  return (
    <AdminSocketContext.Provider value={value}>{children}</AdminSocketContext.Provider>
  );
}

AdminSocketProvider.propTypes = {
  token: PropTypes.string,
  children: PropTypes.node.isRequired,
};
